import {
  AcceptInviteDto,
  CreateInviteDto,
  InviteDto,
  InviteStatusDto,
  UpdateInviteDto,
} from "@repo/api/invite";
import { useMutation, useQuery, UseQueryOptions } from "@tanstack/react-query";
import { InviteApi } from "../api/invite-api";

export function useInviteStatusQuery(
  inviteExternalId: string,
  options?: Partial<UseQueryOptions<InviteStatusDto>>,
) {
  return useQuery({
    queryKey: inviteQueryKeys.getInviteStatus(inviteExternalId),
    queryFn: () => InviteApi.getInviteStatus(inviteExternalId),
    staleTime: Infinity,
    ...options,
  });
}

export function useAcceptInviteMutation({
  inviteExternalId,
  onSuccess,
  onError,
}: {
  inviteExternalId: string;
  onSuccess?: (data: AcceptInviteDto) => void;
  onError?: (error: Error) => void;
}) {
  return useMutation({
    mutationKey: inviteQueryKeys.acceptInvite(inviteExternalId),
    mutationFn: () => InviteApi.acceptInvite(inviteExternalId),
    onSuccess,
    onError,
    meta: {
      errorMessage: "Failed to accept invite",
    },
  });
}

export function useCreateInviteMutation({
  teamExternalId,
  onSuccess,
  onError,
}: {
  teamExternalId: string;
  onSuccess?: (data: InviteDto) => void;
  onError?: (error: Error) => void;
}) {
  return useMutation({
    mutationKey: inviteQueryKeys.createInvite(teamExternalId),
    mutationFn: (data: CreateInviteDto) =>
      InviteApi.createInvite(data, teamExternalId),
    onSuccess,
    onError,
  });
}

export function useUpdateInviteMutation({
  teamExternalId,
  onSuccess,
  onError,
  onMutate,
}: {
  teamExternalId: string;
  onSuccess?: (data: InviteDto) => void;
  onError?: (
    error: Error,
    variables: {
      inviteExternalId: string;
      data: UpdateInviteDto;
    },
    context: any,
  ) => void;
  onMutate?: (variables: {
    inviteExternalId: string;
    data: UpdateInviteDto;
  }) => Promise<any> | any;
}) {
  return useMutation({
    mutationKey: inviteQueryKeys.updateInvite(teamExternalId),
    mutationFn: (variables: {
      inviteExternalId: string;
      data: UpdateInviteDto;
    }) =>
      InviteApi.updateInvite(
        variables.data,
        teamExternalId,
        variables.inviteExternalId,
      ),
    onSuccess,
    onMutate,
    onError,
  });
}

export function useDeleteInviteMutation({
  teamExternalId,
  onSuccess,
  onError,
}: {
  teamExternalId: string;
  onSuccess?: (data: InviteDto) => void;
  onError?: (error: Error) => void;
}) {
  return useMutation({
    mutationKey: inviteQueryKeys.updateInvite(teamExternalId),
    mutationFn: (inviteExternalId: string) =>
      InviteApi.deleteInvite(inviteExternalId, teamExternalId),
    onSuccess,
    onError,
  });
}

const inviteQueryKeys = {
  createInvite: (teamExternalId: string) => [
    "invite",
    "delete",
    { teamExternalId },
  ],
  updateInvite: (teamExternalId: string) => [
    "invite",
    "update",
    { teamExternalId },
  ],
  deleteInvite: (teamExternalId: string) => [
    "invite",
    "delete",
    { teamExternalId },
  ],
  acceptInvite: (inviteExternalId: string) => [
    "invite",
    "accept-invite",
    { inviteExternalId },
  ],
  getInviteStatus: (inviteExternalId: string) => [
    "invite",
    "invite-status",
    { inviteExternalId },
  ],
};
